import { create } from 'zustand'

export type NotificationTone = 'info' | 'success' | 'error'

export interface Notification {
  id: number
  tone: NotificationTone
  message: string
  /** Where the banner offers to take you — the page that shows the work it is reporting. */
  linkTo?: string
  linkLabel?: string
  /** What the banner offers to do instead of navigating — open the task list, say. Rendered in
   *  the link's place; the banner closes once it has run. */
  actionLabel?: string
  onAction?: () => void
  /** Milliseconds on screen. `null` stays up until it is closed, which is what a failure needs:
   *  the dialog that raised the work is gone, so the banner is the only place it is reported. */
  autoDismissMs: number | null
  /** Runs once the banner has gone, however it went. */
  onDismiss?: () => void
}

/** How long a banner reporting work that is now under way stays up. */
export const DEFAULT_DISMISS_MS = 3000

let nextId = 1

interface NotificationStore {
  items: Notification[]
  notify: (
    n: Omit<Notification, 'id' | 'autoDismissMs'> & { autoDismissMs?: number | null },
  ) => number
  dismiss: (id: number) => void
  /** Drops every banner without running its `onDismiss`. For sign-out: a banner reports work
   *  done by the user who raised it, and must not be shown to the next user. */
  clear: () => void
}

/**
 * Banners raised from anywhere and rendered once, by the Layout.
 *
 * A store rather than component state because the thing that raises a banner is usually on its
 * way out — a modal that closes as it hands work to a worker cannot also keep showing what
 * happened to it.
 */
export const useNotificationStore = create<NotificationStore>((set, get) => ({
  items: [],
  notify: ({ autoDismissMs = DEFAULT_DISMISS_MS, ...rest }) => {
    const id = nextId++
    set((s) => ({ items: [...s.items, { id, autoDismissMs, ...rest }] }))
    return id
  },
  dismiss: (id) => {
    const item = get().items.find((n) => n.id === id)
    if (!item) return
    set((s) => ({ items: s.items.filter((n) => n.id !== id) }))
    item.onDismiss?.()
  },
  clear: () => set({ items: [] }),
}))
